import { useEffect, useEffectEvent, useRef, useState } from "react";

import { MUI_TYPOGRAPHY_VARIANTS } from "./mui-typography-shape";
import { cn } from "@/lib/utils";

import { HtmlBox } from "./html-box";
import { MuiArrowDropDownIcon } from "./mui-arrow-icons";
import { MuiArrowDropUpIcon } from "./mui-spec-icons";

// Notes clamped to two lines with a More/Less link — the donor's
// `SpecSheetNotes` (`src/components/specSheets/SpecSheetNotes.tsx`), at
// `hideIcon`, which is how every call site on the lines body draws it: the
// group header, each row's notes cell, and each location's notes (#695). The
// Editor draws its read-only notes through it too.
//
// **Shared, because the donor's is** (`src/components/specSheets/` is the spec
// sheets' shared folder), and because its one rule has to be the same rule on
// every screen that shows a spec's notes.
//
// That rule is the donor's quirk and it is carried, not fixed: the link shows
// when the **raw HTML string** — tags included — measured on a canvas in the
// element's font, is more than twice the element's width, or when the string
// has more than two newline-separated lines. It measures the markup rather
// than the text a reader sees, so a short note with long markup gets a More
// link and the donor's decision about which notes are "long" is this one.
// Measured on mount, when the notes change, and on window resize; nothing
// else (no `ResizeObserver` in the donor).
//
// The rest, value for value:
//
//   - the outer row (the measured element): `pr 20`, space-between, centred,
//     growing — so the link sits to the right of the text, not under it;
//   - the note: `rgba(0,0,0,0.6)`, left-aligned, wrapping, `ellipsis`,
//     clamped (`-webkit-box`, two lines, clipped) **from mount whatever the
//     measurement** — the donor's `open` starts true and means clamped — and a
//     flex box when opened; every paragraph `margin: 8px`, with the first
//     element of each tag zeroed; `mb 8` while the link shows;
//   - the link: MUI `Link` as a `<button>`, `body2`, 500, `#41434A`, no
//     underline, a non-breaking space then the word and the 24px drop arrow;
//     "Less" 8px clear of the text.
//
// The markup goes through `HtmlBox`, which Python has sanitised; the donor
// sanitises nothing, which is the one thing not ported.

interface TruncatedNotesProps {
  /** Sanitised markup, or nothing. The donor draws an empty note for null. */
  notes: string | null | undefined;
  /** The donor's `sx`, on the outer row. */
  className?: string;
  /** Whether the note starts clamped (the donor's `open`); clamped by default. */
  defaultClamped?: boolean;
  /** Whether the note is clamped — as it mounts, and at each More / Less. */
  onClampedChange?: (clamped: boolean) => void;
}

/**
 * The donor's `getTextWidth`: a fresh canvas and the element's font. The donor
 * sets only the size and family, which measures the same for the upright
 * weight-400 text every call site draws; the style and weight are stated too
 * so a heavier note measures as it renders.
 */
function measureTextWidth(text: string, element: HTMLElement): number {
  const context = document.createElement("canvas").getContext("2d");
  if (!context) return 0;
  const style = getComputedStyle(element);
  context.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
  return context.measureText(text).width;
}

export function TruncatedNotes({
  notes,
  className,
  defaultClamped = true,
  onClampedChange,
}: TruncatedNotesProps) {
  const rowRef = useRef<HTMLDivElement>(null);
  // The donor's name and polarity: `true` is clamped.
  const [open, setOpen] = useState(defaultClamped);
  const report = useEffectEvent((now: boolean) => onClampedChange?.(now));
  useEffect(() => report(open), [open]);
  const [widths, setWidths] = useState({ text: 0, element: 0 });

  useEffect(() => {
    const update = () => {
      const row = rowRef.current;
      if (!row) return;
      // The donor hands `measureText` the field as it is, so a missing note is
      // measured as its printed form — a few characters, never a More link.
      setWidths({ text: measureTextWidth(String(notes), row), element: row.offsetWidth });
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [notes]);

  const isTruncated = widths.text / 2 > widths.element;
  const lineCount = notes ? notes.split("\n").length : 0;
  const showLink = isTruncated || lineCount > 2;

  return (
    <div
      ref={rowRef}
      data-slot="truncated-notes"
      className={cn("flex grow flex-row items-center justify-between pr-5", className)}
    >
      <div
        data-testid="spec-sheet-notes"
        className={cn(
          "text-lennar-ink-secondary text-left text-ellipsis whitespace-normal [-webkit-box-orient:vertical]",
          open ? "[display:-webkit-box] overflow-hidden [-webkit-line-clamp:2]" : "flex",
          showLink ? "mb-2" : "mb-0",
        )}
      >
        <HtmlBox
          html={notes ?? ""}
          className="[word-break:break-word] [&_p]:m-2 [&>:first-of-type]:m-0"
        />
      </div>
      {showLink ? (
        <button
          type="button"
          data-testid={open ? "open-truncated-notes" : "close-truncated-notes"}
          onClick={() => setOpen((current) => !current)}
          className={cn(
            MUI_TYPOGRAPHY_VARIANTS.body2,
            "text-spec-notes-link flex cursor-pointer items-center border-0 bg-transparent p-0 font-medium no-underline select-none",
            !open && "ml-2",
          )}
        >
          {open ? (
            <>
              {" More"}
              <MuiArrowDropDownIcon />
            </>
          ) : (
            <>
              {" Less"}
              <MuiArrowDropUpIcon />
            </>
          )}
        </button>
      ) : null}
    </div>
  );
}
